import { ArrowLeft, ArrowRight, Check, CircleAlert, RefreshCw, ShieldCheck } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import GoogleSignInButton from "../../components/GoogleSignInButton";
import { useAuth } from "../../contexts/AuthContext";
import { analyticsService } from "../../services/analyticsService";
import configService from "../../services/configService";
import { isXInAppBrowser } from "../../utils/inAppBrowser";
import { TrackingEvents } from "../../services/trackingEvents";
import { trackingService } from "../../services/trackingService";
import { getPostAuthDestination } from "../../utils/investorRouteCutover";
import AuthBrandPanel, { PROVIDER_LOGOS } from "./AuthBrandPanel";
import PartnerLoginView from "./PartnerLoginView";
import { getOAuthQuery } from "../../utils/oauthContinuation";
import { readLastLoginPreference } from "../../utils/lastLoginPreference";
import LastUsedBadge from "./LastUsedBadge";
import { createShareSignInTradeAlertsState } from "../../utils/shareSignInTradeAlerts";

type AuthMode = "login" | "signup";

interface AuthPageV2Props {
  mode: AuthMode;
  branding?: "mm-opm";
}

// Default page-version token stamped on AuthPageV2 event payloads. Lets us
// slice PostHog funnels by pre-V2 vs V2 cohorts without changing the
// underlying event names, so existing insights (e.g. the login funnel
// anchored on AUTH_LOGIN_PAGE_VIEW) keep counting, but we can also
// filter to `page_version = v2` to isolate the redesign's behavior.
const AUTH_PAGE_VERSION = "v2" as const;
const PARTNER_OFFER_CONTEXT = {
  partner: "mm-opm",
  offer_codes: "MM45,OPM45",
  complimentary_days: 45,
} as const;

const AuthPageV2: React.FC<AuthPageV2Props> = ({ mode, branding }) => {
  const [lastLogin] = useState(() => mode === "login" ? readLastLoginPreference() : null);
  const [email, setEmail] = useState(() => lastLogin?.method === "email" ? lastLogin.email : "");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"email" | "otp">("email");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const oauthQuery = getOAuthQuery(location.search);
  const fromHomepage = new URLSearchParams(location.search).get("origin") === "homepage";
  const { sendOtp, verifyOtp, user, isAuthenticated, isLoading: authLoading, getDefaultLandingPage } = useAuth();
  const awaitedSignIn = useRef(false);

  const isSignup = mode === "signup";
  const isPartnerLogin = branding === "mm-opm" && !isSignup;
  const pageVersion = isPartnerLogin ? "mm-opm-v1" : AUTH_PAGE_VERSION;

  // Cohort tagging: register `page_version` and `mode` as session
  // super properties for the duration of this mount. PostHog merges
  // super properties into every subsequent capture, so EVERY auth
  // event fired while this component is mounted -- including events
  // emitted from shared services (`analyticsService.trackLoginStart`,
  // `AuthContext.sendOtp`, `GoogleSignInButton`, etc.) that do not
  // accept a per-call metadata bag -- automatically carries the
  // cohort tag. Without this, a PostHog funnel filtered on
  // `page_version = "v2"` drops all users after the page-view step
  // because downstream events (`AUTH_*_OTP_SENT`, `AUTH_*_SUCCESS`,
  // etc.) are emitted bare.
  //
  // Session scope (rather than persistent register) caps worst-case
  // leakage at the current browser session if cleanup never runs;
  // the unregister in the effect cleanup handles the happy path so
  // post-auth events on `/dashboard` are not tagged.
  //
  // This effect runs BEFORE the tracking effect below because
  // useEffects run in declaration order; the super properties are
  // live before `AUTH_*_PAGE_VIEW` / `AUTH_SOCIAL_PROOF_VIEW` fire.
  useEffect(() => {
    const mode = isSignup ? "signup" : "login";
    trackingService.registerSessionSuperProperties({
      page_version: pageVersion,
      mode,
    });
    return () => {
      trackingService.unregisterSuperProperty("page_version");
      trackingService.unregisterSuperProperty("mode");
    };
  }, [isSignup, pageVersion]);

  // Funnel start: fire once per mount and per mode. Login mode emits
  // AUTH_LOGIN_PAGE_VIEW + legacy GA login_start; signup mode emits
  // AUTH_SIGNUP_PAGE_VIEW + legacy GA signup_start. Keeps parity with
  // the pre-V2 OTPLoginPage / SignupPage tracking so PostHog funnels
  // continue to anchor on the same events.
  //
  // Both page-view events carry the page version and `mode`
  // explicitly -- duplicative with the super properties above, but
  // intentional: (1) makes the cohort tag visible at the component
  // boundary in tests that mock `trackingService.track`; (2) provides
  // a fallback if super-property registration somehow fails (e.g.
  // PostHog not yet initialized). On the default layout, AUTH_SOCIAL_PROOF_VIEW fires
  // alongside to attribute conversion lift to the provider-logo
  // strip specifically, and carries the stable provider slug list so
  // downstream queries stay comparable across releases.
  const hasTrackedStart = useRef(false);
  useEffect(() => {
    if (hasTrackedStart.current || authLoading || isAuthenticated) return;
    const mode = isSignup ? "signup" : "login";
    const pageViewPayload = { page_version: pageVersion, mode };
    if (isSignup) {
      analyticsService.trackSignupStart();
      trackingService.track(TrackingEvents.AUTH_SIGNUP_PAGE_VIEW, pageViewPayload);
    } else {
      analyticsService.trackLoginStart();
      trackingService.track(TrackingEvents.AUTH_LOGIN_PAGE_VIEW, pageViewPayload);
    }
    // TrackingEventProps is scalar-only by design (see
    // trackingEvents.ts:~194), so the provider list is serialized as a
    // comma-joined slug string. This keeps PostHog's property UI
    // readable and supports `properties.providers CONTAINS 'citrini'`
    // breakdowns without widening the shared prop type.
    if (isPartnerLogin) {
      trackingService.track(TrackingEvents.AUTH_PARTNER_OFFER_VIEW, {
        ...pageViewPayload,
        ...PARTNER_OFFER_CONTEXT,
      });
    } else {
      trackingService.track(TrackingEvents.AUTH_SOCIAL_PROOF_VIEW, {
        page_version: AUTH_PAGE_VERSION,
        mode,
        variant: "provider_logo_strip",
        provider_count: PROVIDER_LOGOS.length,
        providers: PROVIDER_LOGOS.map(logo => logo.slug).join(","),
      });
    }
    hasTrackedStart.current = true;
  }, [authLoading, isAuthenticated, isSignup, isPartnerLogin, pageVersion]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) awaitedSignIn.current = true;
    if (!authLoading && isAuthenticated && oauthQuery === undefined) {
      const destination = getPostAuthDestination(
        location.state?.from,
        fromHomepage ? getDefaultLandingPage("homepage") : getDefaultLandingPage()
      );
      navigate(destination, {
        replace: true,
        state: awaitedSignIn.current && location.state?.fromProductShare === true
          ? createShareSignInTradeAlertsState(user?.id, destination)
          : undefined,
      });
    }
  }, [isAuthenticated, authLoading, navigate, location.state?.from, location.state?.fromProductShare, user?.id, getDefaultLandingPage, oauthQuery, fromHomepage]);

  if (authLoading) {
    return (
      <div
        className="flex min-h-screen min-h-[100dvh] items-center justify-center bg-v7-canvas font-v7-sans"
        role="status"
        aria-label="Loading account access"
      >
        <span
          className="h-8 w-8 animate-spin rounded-full border-2 border-v7-ink/15 border-t-v7-bronze-dark"
          aria-hidden="true"
        />
      </div>
    );
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Both login and signup hit /send-otp via AuthContext.sendOtp. The
    // backend auto-provisions new users on first verify, so no captcha
    // or dedicated /signup endpoint is required client-side.
    // sendOtp emits the matching AUTH_LOGIN_OTP_SENT / AUTH_SIGNUP_OTP_SENT
    // on success and AUTH_LOGIN_ERROR / AUTH_SIGNUP_ERROR (stage=send_otp)
    // on failure; do not re-emit those here.
    const result = await sendOtp(email, isSignup ? "signup" : "login");
    if (result.success) {
      if (isSignup) {
        analyticsService.trackSignupEmailSubmitted(email);
      } else {
        analyticsService.trackLoginEmailSubmitted(email);
      }
      setStep("otp");
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Emit submitted before verify so the funnel step fires even if the
    // verify path fails. Error event for verify failures is emitted by
    // AuthContext.verifyOtp (flow-aware: signup vs login).
    const submittedEvent = isSignup
      ? TrackingEvents.AUTH_SIGNUP_OTP_SUBMITTED
      : TrackingEvents.AUTH_LOGIN_OTP_SUBMITTED;
    trackingService.track(submittedEvent, {
      email_domain: email.split("@")[1] || "unknown",
    });

    const result = oauthQuery === undefined
      ? await verifyOtp(email, otp, isSignup ? "signup" : "login")
      : await verifyOtp(email, otp, isSignup ? "signup" : "login", oauthQuery);

    if (result.redirected) return;

    if (result.success && result.user) {
      if (isSignup) {
        analyticsService.trackSignupComplete(result.user.id);
      } else {
        const userRole = result.user.roles?.[0] || "investor";
        analyticsService.trackLoginSuccess(result.user.id, userRole);
      }
    } else {
      setError(result.message || "Invalid OTP");
    }
    setIsLoading(false);
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    setError("");

    // Resend uses the same /send-otp path as the initial email submit.
    // sendOtp emits the flow-specific OTP_SENT / ERROR events.
    const result = await sendOtp(email, isSignup ? "signup" : "login");
    if (result.success) {
      startCooldown();
    } else {
      setError(result.message || "Failed to resend OTP");
    }
    setIsLoading(false);
  };

  const startCooldown = () => {
    setResendCooldown(120);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleBackToEmail = () => {
    setStep("email");
    setOtp("");
    setError("");
  };

  const headline = step === "otp" ? (
    <>
      Check your <span className="font-v7-serif font-normal italic">email.</span>
    </>
  ) : isSignup ? (
    <>
      Create your <span className="font-v7-serif font-normal italic">account.</span>
    </>
  ) : (
    <>
      Your access is <span className="font-v7-serif font-normal italic">ready.</span>
    </>
  );

  const emailCtaLabel = isSignup ? "Create account" : "Continue with email";
  const emailLoadingLabel = isSignup ? "Creating account..." : "Sending code...";
  const alternateModeLabel = isSignup ? "Log in" : "Create account";
  const alternateModePrompt = isSignup ? "Already have an account?" : "New to Plutus?";
  const alternateModeParams = new URLSearchParams();
  if (oauthQuery !== undefined) alternateModeParams.set("oauth_query", oauthQuery);
  if (fromHomepage) alternateModeParams.set("origin", "homepage");
  const alternateModeSearch = alternateModeParams.toString();
  const alternateModePath = (isSignup ? "/login" : "/signup")
    + (alternateModeSearch ? `?${alternateModeSearch}` : "");
  // Hidden inside the X app's browser, where Google refuses to sign anyone in (James, 2026-10-09).
  const googleSignInEnabled = configService.isGoogleSignInEnabled() && !isXInAppBrowser();

  if (isPartnerLogin) {
    return (
      <PartnerLoginView
        oauthQuery={oauthQuery}
        step={step}
        email={email}
        otp={otp}
        error={error}
        isLoading={isLoading}
        resendCooldown={resendCooldown}
        googleSignInEnabled={googleSignInEnabled && Boolean(configService.getGoogleClientId())}
        lastLoginMethod={lastLogin?.method}
        onEmailChange={setEmail}
        onOtpChange={setOtp}
        onEmailSubmit={handleEmailSubmit}
        onOtpSubmit={handleOTPSubmit}
        onResendOtp={handleResendOTP}
        onBackToEmail={handleBackToEmail}
        onSupportClick={placement => trackingService.track(TrackingEvents.AUTH_PARTNER_SUPPORT_CLICK, {
          page_version: pageVersion,
          mode: "login",
          ...PARTNER_OFFER_CONTEXT,
          channel: "email",
          placement,
          auth_step: step,
        })}
      />
    );
  }

  const renderEmailForm = () => (
    <div className="rounded-v7-panel border border-v7-ink/15 bg-v7-white p-4 sm:p-6">
      {googleSignInEnabled && (
        <>
          <div className="min-h-11">
            {lastLogin?.method === "google" && (
              <div className="mb-2 flex justify-end"><LastUsedBadge /></div>
            )}
            <GoogleSignInButton oauthQuery={oauthQuery} />
          </div>
          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-v7-ink/15" />
            <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-v7-ink/50">
              or email
            </span>
            <div className="h-px flex-1 bg-v7-ink/15" />
          </div>
        </>
      )}

      <form onSubmit={handleEmailSubmit} className="space-y-4">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <label htmlFor="email" className="text-[13px] leading-[19px] text-v7-ink-soft">
              Email address
            </label>
            {lastLogin?.method === "email" && <LastUsedBadge />}
          </div>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value.toLowerCase())}
            placeholder="you@email.com"
            disabled={isLoading}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "auth-error" : undefined}
            className="min-h-12 w-full rounded-v7-control border border-v7-ink/30 bg-v7-white px-3 text-[15px] leading-[22px] text-v7-ink outline-none placeholder:text-v7-ink/40 transition-colors duration-200 hover:border-v7-ink/60 focus:border-v7-bronze-dark focus:ring-2 focus:ring-v7-bronze-dark/15 disabled:cursor-not-allowed disabled:bg-v7-mist disabled:text-v7-ink/40"
          />
        </div>

        {error && (
          <div
            id="auth-error"
            role="alert"
            className="flex items-start gap-2 rounded-v7-control border border-red-200 bg-red-50 p-3 text-[13px] leading-[19px] text-red-700"
          >
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="flex min-h-12 w-full items-center justify-center gap-2 rounded-v7-control border border-v7-ink bg-v7-ink px-[18px] text-[13px] font-medium tracking-[0.06em] text-v7-white transition-colors duration-200 hover:bg-v7-ink-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark disabled:cursor-not-allowed disabled:border-v7-ink/15 disabled:bg-v7-mist disabled:text-v7-ink/40"
        >
          {isLoading ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-v7-ink/20 border-t-v7-ink" aria-hidden="true" />
              {emailLoadingLabel}
            </>
          ) : (
            <>
              {emailCtaLabel}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </>
          )}
        </button>
      </form>

      <div className="mt-5 flex items-start gap-2.5 border-l-2 border-v7-bronze bg-v7-paper p-3 text-xs leading-[17px] text-v7-ink/60">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-v7-bronze-dark" aria-hidden="true" />
        <span>Passwordless by design. Your code expires after 10 minutes.</span>
      </div>
    </div>
  );

  const renderOtpForm = () => (
    <div className="rounded-v7-panel border border-v7-ink/15 bg-v7-white p-4 sm:p-6">
      <form onSubmit={handleOTPSubmit} className="space-y-5">
        <button
          type="button"
          onClick={handleBackToEmail}
          className="inline-flex min-h-11 items-center gap-2 text-[13px] text-v7-ink-soft transition-colors duration-200 hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Edit email
        </button>

        <p id="otp-description" className="text-[13px] leading-[19px] text-v7-ink/60">
          We sent a 6-digit code to <span className="font-medium text-v7-ink">{email}</span>.
        </p>

        <div>
          <label htmlFor="otp" className="mb-2 block text-[13px] leading-[19px] text-v7-ink-soft">
            One-time code
          </label>
          <input
            id="otp"
            name="otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            maxLength={6}
            value={otp}
            onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
            placeholder="000000"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "otp-description auth-error" : "otp-description"}
            className="min-h-12 w-full rounded-v7-control border border-v7-ink/30 bg-v7-white px-3 text-center font-mono text-2xl tracking-[0.45em] text-v7-ink outline-none placeholder:text-v7-ink/30 transition-colors duration-200 hover:border-v7-ink/60 focus:border-v7-bronze-dark focus:ring-2 focus:ring-v7-bronze-dark/15"
          />
        </div>

        {error && (
          <div
            id="auth-error"
            role="alert"
            className="flex items-start gap-2 rounded-v7-control border border-red-200 bg-red-50 p-3 text-[13px] leading-[19px] text-red-700"
          >
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || otp.length !== 6}
          className="flex min-h-12 w-full items-center justify-center gap-2 rounded-v7-control border border-v7-ink bg-v7-ink px-[18px] text-[13px] font-medium tracking-[0.06em] text-v7-white transition-colors duration-200 hover:bg-v7-ink-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark disabled:cursor-not-allowed disabled:border-v7-ink/15 disabled:bg-v7-mist disabled:text-v7-ink/40"
        >
          {isLoading ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-v7-ink/20 border-t-v7-ink" aria-hidden="true" />
              Verifying...
            </>
          ) : (
            <>
              Verify &amp; continue
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </>
          )}
        </button>

        <div className="flex min-h-11 items-center justify-between gap-3 text-xs text-v7-ink/60">
          <span>Did not receive it?</span>
          <button
            type="button"
            onClick={handleResendOTP}
            disabled={isLoading || resendCooldown > 0}
            className="inline-flex min-h-11 items-center gap-1.5 text-v7-ink-soft underline underline-offset-4 transition-colors duration-200 hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark disabled:cursor-not-allowed disabled:text-v7-ink/35 disabled:no-underline"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            {resendCooldown > 0
              ? `Resend in ${Math.floor(resendCooldown / 60)}:${(resendCooldown % 60).toString().padStart(2, "0")}`
              : "Resend code"}
          </button>
        </div>

        <div className="border-t border-v7-ink/15 pt-5">
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.14em] text-v7-ink/50">
            Didn&apos;t get it?
          </p>
          <div className="space-y-2 text-xs leading-[17px] text-v7-ink/60">
            <div className="flex items-start gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-v7-ink/40" aria-hidden="true" />
              <span>Check spam / promotions</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-v7-ink/40" aria-hidden="true" />
              <span>Codes expire after 10 minutes</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-v7-ink/40" aria-hidden="true" />
              <a
                href="mailto:hello@runplutus.com"
                className="text-v7-ink-soft underline underline-offset-4 transition-colors duration-200 hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
              >
                Email hello@runplutus.com
              </a>
            </div>
          </div>
        </div>
      </form>
    </div>
  );

  return (
    <div className="grid min-h-screen min-h-[100dvh] bg-v7-canvas font-v7-sans text-v7-ink lg:grid-cols-[minmax(380px,0.88fr)_minmax(560px,1.12fr)]">
      <a
        href="#auth-content"
        className="fixed left-3 top-3 z-[1000] -translate-y-24 border border-v7-ink bg-v7-white px-3.5 py-2.5 text-[13px] text-v7-ink transition-transform duration-200 focus:translate-y-0"
      >
        Skip to account access
      </a>

      <AuthBrandPanel />

      <main
        id="auth-content"
        className="flex min-w-0 flex-col bg-v7-canvas px-4 pb-8 pt-5 sm:px-8 lg:min-h-screen lg:min-h-[100dvh] lg:px-10 lg:py-8"
      >
        <nav className="flex min-h-11 items-center justify-between gap-3 lg:justify-end" aria-label="Account access">
          <span className="text-[13px] text-v7-ink/60">{alternateModePrompt}</span>
          <Link
            to={alternateModePath}
            state={location.state?.fromProductShare === true ? location.state : undefined}
            className="inline-flex min-h-11 items-center justify-center rounded-v7-control border border-v7-ink/30 bg-transparent px-[18px] text-[13px] font-medium tracking-[0.06em] text-v7-ink transition-colors duration-200 hover:border-v7-ink hover:bg-v7-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
          >
            {alternateModeLabel}
          </Link>
        </nav>

        <div className="mx-auto w-full max-w-[470px] py-12 lg:my-auto lg:py-10">
          <header className="mb-8">
            <h1
              id="auth-title"
              className="max-w-[11ch] text-[38px] font-light leading-none tracking-[-0.05em] text-v7-ink sm:text-[44px] lg:text-[52px]"
            >
              {headline}
            </h1>
          </header>

          {step === "email" ? renderEmailForm() : renderOtpForm()}

          <p className="mt-5 text-left text-[11px] font-light leading-[17px] text-v7-ink/45">
            By continuing, you agree to our{" "}
            <a
              href="/terms"
              className="text-v7-ink-soft underline underline-offset-4 transition-colors duration-200 hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
            >
              Terms
            </a>{" "}
            and{" "}
            <a
              href="/privacy"
              className="text-v7-ink-soft underline underline-offset-4 transition-colors duration-200 hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
            >
              Privacy Policy
            </a>
            . Advisory services by Namsoft Advisors LLC, an SEC-registered investment advisor.
          </p>
        </div>
      </main>
    </div>
  );
};

export default AuthPageV2;
